import { useEffect, useRef, useState } from "react";

interface ShareModalProps {
  title: string;
  onClose: () => void;
}

const socials = [
  { label: "微博", icon: "ri-weibo-fill" },
  { label: "微信", icon: "ri-wechat-fill" },
  { label: "X", icon: "ri-twitter-x-fill" },
];

export default function ShareModal({ title, onClose }: ShareModalProps) {
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "failed">("idle");
  const [demoHint, setDemoHint] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const link = typeof window !== "undefined" ? window.location.href : "";

  const handleCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        inputRef.current?.select();
        const ok = document.execCommand("copy");
        if (!ok) throw new Error("copy failed");
      }
      setCopyStatus("copied");
    } catch {
      setCopyStatus("failed");
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-primary-950/60" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-title"
        className="rk-fade-up relative w-full max-w-md rounded-xl bg-background-50 p-6"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-label text-[11px] uppercase tracking-[0.2em] text-foreground-500">Share</p>
            <h2 id="share-title" className="mt-1.5 font-heading text-lg font-semibold text-foreground-950">
              分享《{title}》
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭分享弹窗"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-600 transition-colors hover:text-foreground-900"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <p className="mt-3 text-xs leading-relaxed text-foreground-500">
          复制页面链接分享给朋友。社交平台分享按钮为演示功能，暂未接入外部接口。
        </p>

        <div className="mt-4 flex items-center gap-2 rounded-md border border-background-300 bg-background-100 p-1.5">
          <input
            ref={inputRef}
            readOnly
            value={link}
            aria-label="页面链接"
            className="w-full min-w-0 bg-transparent px-2 text-xs text-foreground-700 outline-none"
          />
          <button
            type="button"
            onClick={handleCopy}
            className="shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-primary-950 px-3.5 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-800"
          >
            {copyStatus === "copied" ? "已复制" : "复制链接"}
          </button>
        </div>

        {copyStatus === "copied" && (
          <p className="mt-2 flex items-center gap-1 text-[11px] text-primary-700">
            <i className="ri-check-line text-sm" />
            链接已复制到剪贴板
          </p>
        )}
        {copyStatus === "failed" && (
          <p className="mt-2 flex items-center gap-1 text-[11px] text-accent-800">
            <i className="ri-error-warning-line text-sm" />
            复制失败，请手动选中上方链接复制
          </p>
        )}

        <div className="mt-5 grid grid-cols-3 gap-2">
          {socials.map((social) => (
            <button
              key={social.label}
              type="button"
              onClick={() => setDemoHint(`${social.label}分享为演示功能，暂未接入外部接口。`)}
              className="flex cursor-pointer flex-col items-center gap-1.5 rounded-md border border-background-200 bg-background-50 py-3 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className={`${social.icon} text-lg`} />
              {social.label}
            </button>
          ))}
        </div>

        {demoHint && (
          <p className="mt-3 rounded-md bg-secondary-50 px-3 py-2 text-[11px] leading-relaxed text-secondary-800">
            {demoHint}
          </p>
        )}
      </div>
    </div>
  );
}